import { statefulPost } from '@api-client/csrf';
import { getOrCreateDeviceToken } from '@/utils/deviceTrust';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Checkbox,
  Heading,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  REGEXP_ONLY_DIGITS,
  Text
} from 'stubben-edge-unified-frontend-react-components';

interface Props {
  apiUrl: string;
  title: string;
  currentStepIndex: number;
  setCurrentStepIndex: (e: number) => void;
  mfaOption: string;
  bearerToken: string;
  setBearerToken: (e: string) => void;
  setTenants: (e: any) => void;
  setCompany: (e: any) => void;
}

const MfaStep: FC<Props> = ({
  apiUrl,
  title,
  currentStepIndex,
  setCurrentStepIndex,
  mfaOption,
  bearerToken,
  setBearerToken,
  setCompany,
  setTenants
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get('uuid') as string;
  const [error, setError] = useState<string>('');
  const [trustDevice, setTrustDevice] = useState<boolean>(false);

  const handleEnter = async (code: string) => {
    try {
      const res = await statefulPost(
        `${apiUrl}/mfa/verify`,
        {
          otp: code,
          trust_device: trustDevice
        },
        {
          Authorization: `Bearer ${bearerToken}`,
          'X-Device-Token': getOrCreateDeviceToken()
        }
      );

      setBearerToken(res.data.token);

      const company = res.data.tenants.find(
        (tenant: any) => tenant.company.uuid === uuid
      );

      if (company) {
        setCompany(company.company);
        setCurrentStepIndex(currentStepIndex + 2);
      } else if (res.data.tenants.length > 1) {
        setTenants(res.data.tenants);
        setCurrentStepIndex(currentStepIndex + 1);
      } else {
        setCompany(res.data.tenants[0].company);
        setCurrentStepIndex(currentStepIndex + 2);
      }
    } catch (err: any) {
      toast.error('Oops, something went wrong!');

      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (
        err.response?.data?.errors?.message === 'Invalid code received for user'
      ) {
        setError('Invalid verification code');
      } else {
        setError(err.response?.data?.errors?.message ?? 'Something went wrong');
      }
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Heading size="xs" testIdentifier="login-mfa-heading">
            {title}
          </Heading>
          <Text>
            {mfaOption === 'app'
              ? 'Please enter the MFA code in your authenticator app.'
              : 'Please enter the MFA code sent to your mobile device.'}
          </Text>
        </div>
        <div className="flex flex-col gap-2" data-testid="login-mfa-code">
          <InputOTP
            maxLength={6}
            pattern={REGEXP_ONLY_DIGITS}
            onComplete={(value) => handleEnter(value)}
            autoFocus
          >
            <InputOTPGroup>
              {Array.from({ length: 6 }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className={error ? 'border-2 border-error' : ''}
                  activeClassName={'rounded border-2 !border-[#3b82f680]'}
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
          {error && (
            <Text size="xs" className="text-error">
              {error}
            </Text>
          )}
        </div>
      </div>
      <Text size="sm">
        Don’t have your MFA code? <a href="/login">Go back to login</a>
      </Text>
      <Checkbox
        label="Trust this device for 7 days."
        onChange={() => setTrustDevice(!trustDevice)}
        checked={trustDevice}
        testIdentifier="login-mfa-trust-device"
      />
      <div className="flex flex-col gap-4">
        <Text size="sm">
          Trouble logging in? Email our support team{' '}
          <a href="mailto:support@stubbenedge.com" className="text-[#2A54EB]">
            support@stubbenedge.com
          </a>
        </Text>
        <Text variant="secondary" size="xs">
          This system is restricted to authorised users only. Individuals who
          attempt unauthorised access will be prosecuted.
        </Text>
      </div>
    </div>
  );
};

export default MfaStep;
